import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../shared/components/ProtectedRoute'
import UserHeader from '../components/UserHeader'
import {
  CameraIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon,
  LinkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { apiService } from '../../../shared/services/api'
import toast from 'react-hot-toast'

interface ProfileData {
  firstName?: string
  lastName?: string
  name?: string
  email: string
  username: string
  bio?: string
  website?: string
  location?: string
  avatar?: string
  plan: string
  role: string
  preferences: {
    theme: 'light' | 'dark' | 'system'
    language: string
    notifications: {
      email: boolean
      push: boolean
      reports: boolean
    }
    privacy: {
      publicProfile: boolean
      showStats: boolean
    }
  }
}

const ProfilePage: React.FC = () => {
  const { user, login } = useAuth()
  const [profileData, setProfileData] = useState<ProfileData | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setIsLoading(true)
      const profile = await apiService.getUserProfile()
      const profileWithDefaults = profile as any

      setProfileData({
        firstName: profileWithDefaults.firstName || '',
        lastName: profileWithDefaults.lastName || '',
        name: profileWithDefaults.name || profileWithDefaults.username || '',
        email: profileWithDefaults.email || '',
        username: profileWithDefaults.username || '',
        bio: profileWithDefaults.bio || '',
        website: profileWithDefaults.website || '',
        location: profileWithDefaults.location || '',
        avatar: profileWithDefaults.avatar || '',
        plan: profileWithDefaults.plan || 'free',
        role: profileWithDefaults.role || 'user',
        preferences: {
          theme: profileWithDefaults.preferences?.theme || 'system',
          language: profileWithDefaults.preferences?.language || 'en',
          notifications: {
            email:
              profileWithDefaults.preferences?.notifications?.email ?? true,
            push: profileWithDefaults.preferences?.notifications?.push ?? false,
            reports:
              profileWithDefaults.preferences?.notifications?.reports ?? true,
          },
          privacy: {
            publicProfile:
              profileWithDefaults.preferences?.privacy?.publicProfile ?? false,
            showStats:
              profileWithDefaults.preferences?.privacy?.showStats ?? true,
          },
        },
      })
    } catch (error) {
      console.error('Error loading profile:', error)
      toast.error('Failed to load profile')
      // Fallback to context user data
      if (user) {
        setProfileData({
          firstName: '',
          lastName: '',
          name: user.name || user.username || '',
          email: user.email || '',
          username: user.username || '',
          bio: '',
          website: '',
          location: '',
          avatar: '',
          plan: user.plan || 'free',
          role: user.role || 'user',
          preferences: {
            theme: 'system',
            language: 'en',
            notifications: {
              email: true,
              push: false,
              reports: true,
            },
            privacy: {
              publicProfile: false,
              showStats: true,
            },
          },
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    if (!profileData) {
      return
    }

    try {
      setIsSaving(true)
      const updatedUser = await apiService.updateUserProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        name: profileData.name,
        bio: profileData.bio,
        website: profileData.website,
        location: profileData.location,
        preferences: profileData.preferences,
      })

      // Update user context with new token and user data
      login(localStorage.getItem('token') || '', updatedUser)
      setIsEditing(false)
      toast.success('Profile updated successfully')
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'premium':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
      case 'pro':
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
      case 'scale':
        return 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
      case 'enterprise':
        return 'bg-gradient-to-r from-gray-700 to-gray-900 text-white'
      case 'free':
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const getPlanName = (plan: string) => {
    switch (plan) {
      case 'premium':
        return 'Premium'
      case 'pro':
        return 'Pro'
      case 'scale':
        return 'Scale'
      case 'enterprise':
        return 'Enterprise'
      case 'free':
      default:
        return 'Free'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <UserHeader
          title="Profile"
          subtitle="Manage your account settings and preferences"
        />
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <UserHeader
          title="Profile"
          subtitle="Manage your account settings and preferences"
        />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <InformationCircleIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400">
              Failed to load profile data
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <UserHeader
        title="Profile"
        subtitle="Manage your account settings and preferences"
      />

      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Profile Header */}
          <div className="rounded-lg bg-white shadow dark:bg-gray-800">
            <div className="px-6 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  {/* Avatar */}
                  <div className="relative">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
                      {profileData.avatar ? (
                        <img
                          src={profileData.avatar}
                          alt="Profile"
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl font-semibold text-white">
                          {profileData.name?.charAt(0)?.toUpperCase() ||
                            profileData.username?.charAt(0)?.toUpperCase() ||
                            'U'}
                        </span>
                      )}
                    </div>
                    <button className="absolute bottom-0 right-0 rounded-full bg-blue-600 p-2 text-white transition-colors hover:bg-blue-700">
                      <CameraIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* User Info */}
                  <div>
                    <div className="flex items-center space-x-3">
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {profileData.name || profileData.username}
                      </h1>
                      <span
                        className={`rounded-full px-3 py-1 text-sm font-medium ${getPlanBadgeColor(profileData.plan)}`}
                      >
                        {getPlanName(profileData.plan)}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                      @{profileData.username}
                    </p>
                    <p className="text-gray-500 dark:text-gray-500">
                      {profileData.email}
                    </p>
                  </div>
                </div>

                {/* Edit Button */}
                <div className="flex space-x-2">
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <PencilIcon className="mr-2 h-4 w-4" />
                      Edit Profile
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveProfile}
                        disabled={isSaving}
                        className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        <CheckIcon className="mr-2 h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <XMarkIcon className="mr-2 h-4 w-4" />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="rounded-lg bg-white shadow dark:bg-gray-800">
            <div className="px-6 py-6">
              <h2 className="mb-6 text-lg font-medium text-gray-900 dark:text-white">
                Profile Information
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={profileData.firstName || ''}
                    onChange={e =>
                      setProfileData({
                        ...profileData,
                        firstName: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={profileData.lastName || ''}
                    onChange={e =>
                      setProfileData({
                        ...profileData,
                        lastName: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={profileData.name || ''}
                    onChange={e =>
                      setProfileData({ ...profileData, name: e.target.value })
                    }
                    disabled={!isEditing}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bio
                  </label>
                  <textarea
                    value={profileData.bio || ''}
                    onChange={e =>
                      setProfileData({ ...profileData, bio: e.target.value })
                    }
                    disabled={!isEditing}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <LinkIcon className="mr-1 inline h-4 w-4" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={profileData.website || ''}
                    onChange={e =>
                      setProfileData({
                        ...profileData,
                        website: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <MapPinIcon className="mr-1 inline h-4 w-4" />
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.location || ''}
                    onChange={e =>
                      setProfileData({
                        ...profileData,
                        location: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                    placeholder="Seoul, South Korea"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preferences Section */}
          <div className="rounded-lg bg-white shadow dark:bg-gray-800">
            <div className="px-6 py-6">
              <h2 className="mb-6 text-lg font-medium text-gray-900 dark:text-white">
                Preferences
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Theme
                  </label>
                  <select
                    value={profileData.preferences.theme}
                    onChange={e =>
                      setProfileData({
                        ...profileData,
                        preferences: {
                          ...profileData.preferences,
                          theme: e.target.value as 'light' | 'dark' | 'system',
                        },
                      })
                    }
                    disabled={!isEditing}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="system">System</option>
                  </select>
                </div>

                <div>
                  <label className="mb-4 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Privacy Settings
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Public Profile
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Allow others to see your profile
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          setProfileData({
                            ...profileData,
                            preferences: {
                              ...profileData.preferences,
                              privacy: {
                                ...profileData.preferences.privacy,
                                publicProfile:
                                  !profileData.preferences.privacy
                                    .publicProfile,
                              },
                            },
                          })
                        }
                        disabled={!isEditing}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          profileData.preferences.privacy.publicProfile
                            ? 'bg-blue-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        } ${!isEditing ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            profileData.preferences.privacy.publicProfile
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
